import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const SUPPORTED = ['fr', 'en', 'es', 'de'];

// Compute the language a visitor on "/" should be sent to.
const resolveTargetLang = (search) => {
  // 1. Old URLs with ?lang=xx (e.g. /?lang=en) take priority for SEO consolidation
  const langParam = new URLSearchParams(search).get('lang');
  if (langParam && SUPPORTED.includes(langParam)) {
    return langParam;
  }

  // 2. Previously saved preference
  const savedLang = localStorage.getItem('language');
  if (savedLang && SUPPORTED.includes(savedLang)) {
    return savedLang;
  }

  // 3. Browser language, default to French
  const browserLang = (navigator.language || navigator.userLanguage || 'fr').split('-')[0];
  return SUPPORTED.includes(browserLang) ? browserLang : 'fr';
};

/**
 * Handles requests to the root path "/" (including legacy "/?lang=xx" URLs).
 *
 * On Emergent static hosting the server returns HTTP 200 for "/", so a true
 * server-side 301 is not possible. We therefore emit an INSTANT meta refresh
 * (content="0"), which Google Search treats as a *permanent* redirect
 * (equivalent to a 301), plus a window.location.replace fallback for browsers.
 */
const LanguageRedirect = () => {
  const location = useLocation();
  const [target] = useState(() => {
    const lang = resolveTargetLang(location.search);
    localStorage.setItem('language', lang);
    const url = `/${lang}/`;
    // Trigger the real navigation immediately for browsers.
    if (typeof window !== 'undefined' && window.location.pathname === '/') {
      window.location.replace(url);
    }
    return url;
  });

  return (
    <Helmet>
      <meta http-equiv="refresh" content={`0; url=${target}`} />
      <link rel="canonical" href={`https://a-more-ai.com${target}`} />
      <meta name="robots" content="noindex, follow" />
    </Helmet>
  );
};

export default LanguageRedirect;
